import { useState } from "react";
import { supabase } from "../services/supabase";
import { validateEmail } from "../utils/validators";
import AuthLayout from "../components/AuthLayout";


export default function ErrorScreen({ errorType, onBackToLogin }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [linkType, setLinkType] = useState("signup"); // signup | recovery

  /* ---------------- Error Content ---------------- */
  const getErrorInfo = () => {
    switch (errorType) {
      case "otp_expired":
        return {
          title: "Link Expired",
          description:
            "The verification link has expired. Verification links are valid for 24 hours.",
        };
      case "access_denied":
        return {
          title: "Access Denied",
          description:
            "The verification link is invalid or has already been used.",
        };
      default:
        return {
          title: "Something Went Wrong",
          description:
            "An error occurred during verification. Please try again.",
        };
    }
  };

  /* ---------------- Resend Handler ---------------- */
  const handleResendLink = async () => {
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }

    setError("");
    setSending(true);

    try {
      if (linkType === "signup") {
        await supabase.auth.resend({
          type: "signup",
          email,
        });
      } else {
        await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/login`,
        });
      }
      setSent(true);
    } catch (err) {
      setError(
        err.message ||
          "Failed to send email. Please make sure the email is correct."
      );
    } finally {
      setSending(false);
    }
  };

  const errorInfo = getErrorInfo();

  /* ---------------- Success State ---------------- */
  if (sent) {
    return (
      <AuthLayout>

        <main className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <h1 className="text-3xl font-bold mb-4 text-gray-800">
              Email Sent!
            </h1>
            <p className="text-gray-500 mb-6">
              Please check your inbox and click the link we sent.
            </p>

            <button
              onClick={onBackToLogin}
              className="w-full bg-[#06402B] text-white py-3 rounded-full"
            >
              Back to Login
            </button>
          </div>
        </main>
      </AuthLayout>
    );
  }

  /* ---------------- Error UI ---------------- */
  return (
    <AuthLayout>

      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              {errorInfo.title}
            </h1>
            <p className="text-gray-500 mb-6">
              {errorInfo.description}
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium mb-3">📧 Need a new link?</p>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                onClick={() => setLinkType("signup")}
                className={`py-2 rounded-full text-sm ${
                  linkType === "signup"
                    ? "bg-[#06402B] text-white"
                    : "border border-gray-300"
                }`}
              >
                Account Verification
              </button>
              <button
                onClick={() => setLinkType("recovery")}
                className={`py-2 rounded-full text-sm ${
                  linkType === "recovery"
                    ? "bg-[#06402B] text-white"
                    : "border border-gray-300"
                }`}
              >
                Password Reset
              </button>
            </div>

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleResendLink()}
              placeholder="Enter your email"
              className={`w-full px-4 py-2 rounded-full border ${
                error ? "border-red-500" : "border-gray-300"
              }`}
            />

            {error && (
              <p className="text-red-500 text-xs mt-2">{error}</p>
            )}

            <button
              onClick={handleResendLink}
              disabled={sending}
              className="w-full mt-4 bg-[#06402B] text-white py-2 rounded-full disabled:opacity-50"
            >
              {sending ? "Sending..." : "Send Link"}
            </button>
          </div>

          <button
            onClick={onBackToLogin}
            className="w-full text-center text-gray-700 hover:underline"
          >
            ← Back to Login
          </button>
        </div>
      </main>
</AuthLayout>
  );
}
